// @flow

import React, { PureComponent } from 'react';

import Avatar from '../../../base/avatar/components/Avatar';
import { openDialog } from '../../../base/dialog';
import { getParticipantById } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { isGifMessage } from '../../../gifs/function.any';
import VideoLayout from '../../../video-layout/VideoLayout';
import { dismissFloatingChatNotification } from '../../actions.any';
import { openChat } from '../../actions.web';

import HideNotificationDialog from './HideNotificationDialog';

const AUTO_DISMISS_TIMEOUT = 5000;
const MAX_LIFESPAN_TIMEOUT = 15000;

type Props = {

    /**
     * Whether or not the chat panel is currently open.
     */
    _isOpen: boolean,

    /**
     * The array of stacked floating chat notifications from Redux.
     */
    _notifications: Array<Object>,

    /**
     * The participant who sent the latest message, if found in Redux.
     */
    _participant: ?Object,

    /**
     * Whether floating chat notifications are hidden.
     */
    _areChatNotificationsHidden: boolean,

    /**
     * Redux dispatch function.
     */
    dispatch: Function
};

type State = {

    /**
     * Unique key to trigger re-animation on new messages.
     */
    animKey: number,

    /**
     * Whether the notification stack is playing its exit animation.
     */
    isExiting: boolean
};

/**
 * Implements a floating stacked toast notification for incoming chat messages
 * when the chat panel is closed (Google Meet & macOS notification stack style).
 */
class FloatingChatNotification extends PureComponent<Props, State> {
    _dismissTimeout: ?TimeoutID = null;
    _exitAnimationTimeout: ?TimeoutID = null;
    _maxLifespanTimeout: ?TimeoutID = null;
    _isHovered: boolean = false;

    /**
     * Initializes a new {@code FloatingChatNotification} instance.
     *
     * @param {Props} props - The component props.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            isExiting: false,
            animKey: Date.now()
        };

        this._onClick = this._onClick.bind(this);
        this._onClose = this._onClose.bind(this);
        this._onMouseEnter = this._onMouseEnter.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._onWindowBlur = this._onWindowBlur.bind(this);
    }

    /**
     * Sets up event listeners and initial dismiss timer.
     *
     * @inheritdoc
     */
    componentDidMount() {
        window.addEventListener('blur', this._onWindowBlur);
        document.addEventListener('mouseleave', this._onWindowBlur);

        if (this.props._notifications?.length > 0 && !this.props._isOpen) {
            this._startDismissTimer();
        }
    }

    /**
     * Handles updates when new floating notifications arrive.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        const { _isOpen, _notifications } = this.props;

        // If chat was opened, clear immediately
        if (_isOpen) {
            this._clearTimeouts();

            return;
        }

        const count = _notifications?.length || 0;
        const prevCount = prevProps._notifications?.length || 0;

        // New notifications arrived
        if (count > 0 && (count !== prevCount || _notifications !== prevProps._notifications)) {
            this._clearTimeouts();
            this.setState({
                isExiting: false,
                animKey: Date.now()
            });

            this._startDismissTimer();
        } else if (count === 0 && prevCount > 0) {
            this._clearTimeouts();
        }
    }

    /**
     * Cleans up event listeners and timeouts on unmount.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        window.removeEventListener('blur', this._onWindowBlur);
        document.removeEventListener('mouseleave', this._onWindowBlur);
        this._clearTimeouts();
    }

    /**
     * Clears all pending dismiss, exit, and max-lifespan timeouts.
     *
     * @returns {void}
     */
    _clearTimeouts() {
        if (this._dismissTimeout) {
            clearTimeout(this._dismissTimeout);
            this._dismissTimeout = null;
        }
        if (this._exitAnimationTimeout) {
            clearTimeout(this._exitAnimationTimeout);
            this._exitAnimationTimeout = null;
        }
        if (this._maxLifespanTimeout) {
            clearTimeout(this._maxLifespanTimeout);
            this._maxLifespanTimeout = null;
        }
    }

    /**
     * Starts the auto-dismiss timer and max lifespan safety timer.
     *
     * @returns {void}
     */
    _startDismissTimer() {
        if (this._dismissTimeout) {
            clearTimeout(this._dismissTimeout);
            this._dismissTimeout = null;
        }

        if (!this._isHovered) {
            this._dismissTimeout = setTimeout(() => {
                this._startExit();
            }, AUTO_DISMISS_TIMEOUT);
        }

        if (!this._maxLifespanTimeout) {
            this._maxLifespanTimeout = setTimeout(() => {
                this._startExit();
            }, MAX_LIFESPAN_TIMEOUT);
        }
    }

    /**
     * Triggers the smooth exit animation and then dispatches dismiss.
     *
     * @returns {void}
     */
    _startExit() {
        this._clearTimeouts();
        this.setState({ isExiting: true });

        this._exitAnimationTimeout = setTimeout(() => {
            this.setState({ isExiting: false });
            this.props.dispatch(dismissFloatingChatNotification());
        }, 320);
    }

    /**
     * Pauses auto-dismiss on mouse enter.
     *
     * @returns {void}
     */
    _onMouseEnter() {
        this._isHovered = true;
        if (this._dismissTimeout) {
            clearTimeout(this._dismissTimeout);
            this._dismissTimeout = null;
        }
    }

    /**
     * Resumes auto-dismiss on mouse leave.
     *
     * @returns {void}
     */
    _onMouseLeave() {
        this._isHovered = false;
        if (this.props._notifications?.length > 0 && !this.state.isExiting) {
            this._startDismissTimer();
        }
    }

    /**
     * Safety handler when window loses focus or cursor leaves document.
     *
     * @returns {void}
     */
    _onWindowBlur() {
        this._isHovered = false;
        if (this.props._notifications?.length > 0 && !this.state.isExiting) {
            this._startDismissTimer();
        }
    }

    /**
     * Handles clicking on the notification or reply button to open chat.
     *
     * @returns {void}
     */
    _onClick() {
        const { _notifications, _participant, dispatch } = this.props;
        const latest = _notifications?.[_notifications.length - 1];

        this._clearTimeouts();
        this.setState({ isExiting: false });
        dispatch(dismissFloatingChatNotification());

        if (latest?.privateMessage && _participant) {
            dispatch(openChat(_participant));
        } else {
            dispatch(openChat());
        }

        VideoLayout.onResize();
    }

    /**
     * Handles clicking the close 'X' button to dismiss without opening chat.
     *
     * @param {Object} event - The click event.
     * @returns {void}
     */
    _onClose(event: SyntheticEvent<>) {
        event.stopPropagation();
        this._startExit();
        this.props.dispatch(openDialog(HideNotificationDialog));
    }

    /**
     * Formats message text into clean preview string.
     *
     * @param {string} raw - The raw message content.
     * @returns {string} The formatted preview text.
     */
    _formatPreview(raw: ?string): string {
        if (!raw) {
            return '';
        }

        if (isGifMessage(raw)) {
            return '🎞️ GIF';
        }

        if (raw.includes('file-attachment') || raw.includes('<a ')) {
            const match = raw.match(/<a[^>]*class=["']file-attachment["'][^>]*>([^<]+)<\/a>/i)
                || raw.match(/<a[^>]*>([^<]+)<\/a>/i);

            if (match && match[1]) {
                const name = match[1].replace(/^[^\w.-]+/i, '').trim();

                return `📎 ${name || 'Attachment'}`;
            }

            return '📎 File attachment';
        }

        const stripped = raw.replace(/<[^>]*>?/gm, '').trim();

        return stripped;
    }

    /**
     * Formats timestamp into local time string.
     *
     * @param {number} timestamp - The message timestamp.
     * @returns {string} Formatted time string.
     */
    _formatTime(timestamp: ?number): string {
        if (!timestamp) {
            return '';
        }

        const date = new Date(timestamp);

        if (isNaN(date.getTime())) {
            return '';
        }

        return date.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
        });
    }

    /**
     * Renders the floating stacked chat notification component.
     *
     * @inheritdoc
     * @returns {React$Element<any> | null}
     */
    render() {
        const { _isOpen, _notifications, _areChatNotificationsHidden } = this.props;
        const { animKey, isExiting } = this.state;

        if (_isOpen || _areChatNotificationsHidden || !_notifications || _notifications.length === 0) {
            return null;
        }

        const count = _notifications.length;
        const latest = _notifications[count - 1];
        const { displayName, id, message, privateMessage, timestamp } = latest;
        const preview = this._formatPreview(message);
        const timeString = this._formatTime(timestamp);

        return (
            <div
                className = { `floating-chat-stack-container ${isExiting ? 'is-exiting' : 'is-entering'}` }
                key = { animKey }
                onMouseEnter = { this._onMouseEnter }
                onMouseLeave = { this._onMouseLeave }>
                <div className = 'floating-chat-stack-wrapper'>
                    { count >= 3 && <div className = 'floating-chat-stack-layer floating-chat-stack-layer-2' /> }
                    { count >= 2 && <div className = 'floating-chat-stack-layer floating-chat-stack-layer-1' /> }

                    <div
                        aria-label = 'Open chat'
                        className = { `floating-chat-card ${count > 1 ? 'has-stack' : ''}` }
                        onClick = { this._onClick }
                        role = 'button'
                        tabIndex = { 0 }>
                        <div className = 'floating-chat-avatar-wrapper'>
                            <Avatar
                                className = 'avatar'
                                displayName = { displayName }
                                participantId = { id }
                                size = { 38 } />
                        </div>

                        <div className = 'floating-chat-body'>
                            <div className = 'floating-chat-header'>
                                <span
                                    className = 'floating-chat-sender-name'
                                    title = { displayName }>
                                    { displayName || 'Participant' }
                                </span>
                                { count > 1 && (
                                    <span className = 'floating-chat-badge-count'>
                                        { count } messages
                                    </span>
                                ) }
                                { privateMessage && (
                                    <span className = 'floating-chat-badge-private'>
                                        Private
                                    </span>
                                ) }
                                { timeString && (
                                    <span className = 'floating-chat-timestamp'>
                                        { timeString }
                                    </span>
                                ) }
                            </div>
                            <p
                                className = 'floating-chat-message-text'
                                title = { preview }>
                                { preview }
                            </p>
                        </div>

                        <div className = 'floating-chat-actions'>
                            <button
                                aria-label = 'Reply'
                                className = 'floating-chat-reply-btn'
                                onClick = { this._onClick }
                                title = 'Reply'
                                type = 'button'>
                                <svg
                                    fill = 'currentColor'
                                    height = '16'
                                    viewBox = '0 0 24 24'
                                    width = '16'>
                                    <path d = 'M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7h-2z' />
                                </svg>
                            </button>
                            <button
                                aria-label = 'Close'
                                className = 'floating-chat-close-btn'
                                onClick = { this._onClose }
                                title = 'Dismiss'
                                type = 'button'>
                                <svg
                                    fill = 'none'
                                    height = '14'
                                    stroke = 'currentColor'
                                    strokeLinecap = 'round'
                                    strokeLinejoin = 'round'
                                    strokeWidth = '2.5'
                                    viewBox = '0 0 24 24'
                                    width = '14'>
                                    <line
                                        x1 = '18'
                                        x2 = '6'
                                        y1 = '6'
                                        y2 = '18' />
                                    <line
                                        x1 = '6'
                                        x2 = '18'
                                        y1 = '6'
                                        y2 = '18' />
                                </svg>
                            </button>
                        </div>

                        { !isExiting && <div className = 'floating-chat-progress-bar' /> }
                    </div>
                </div>
            </div>
        );
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code FloatingChatNotification}'s props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const chatState = state['features/chat'] || {};
    const notifications = chatState.floatingChatNotifications
        || (chatState.floatingChatNotification ? [ chatState.floatingChatNotification ] : []);
    const latest = notifications[notifications.length - 1];
    const participant = latest?.id
        ? getParticipantById(state, latest.id)
        : null;

    return {
        _isOpen: Boolean(chatState.isOpen),
        _notifications: notifications,
        _participant: participant,
        _areChatNotificationsHidden: Boolean(chatState.areChatNotificationsHidden)
    };
}

export default connect(_mapStateToProps)(FloatingChatNotification);
